import React,{useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import './TeamScreen.css';

const {
  FiCheck,
  FiClock,
  FiMail,
  FiRefreshCw,
  FiTrash2,
  FiUserPlus,
  FiUsers,
  FiX
}=FiIcons;

const roles=['Admin','Editor','Viewer'];

function MemberRow({member,onResend,onCancel}) {
  const pending=member.status==='pending';

  return (
    <motion.article
      className={`team-member-row ${pending ? 'pending' : 'active'}`}
      layout
      initial={{opacity:0,y:8}}
      animate={{opacity:1,y:0}}
    >
      <span className="team-avatar">
        {pending
          ? <SafeIcon icon={FiMail} />
          : member.name.split(' ').map((word)=> word[0]).join('').slice(0,2)}
      </span>

      <div className="team-member-details">
        <strong>{member.name || member.email}</strong>
        <span>{member.email}</span>
      </div>

      <span className="team-role">{member.role}</span>

      <span className={`team-status ${member.status}`}>
        <SafeIcon icon={pending ? FiClock : FiCheck} />
        {pending ? 'Pending' : 'Active'}
      </span>

      <div className="team-member-actions">
        {pending ? (
          <>
            <button
              type="button"
              onClick={()=> onResend(member.id)}
            >
              <SafeIcon icon={FiRefreshCw} />
              Resend
            </button>
            <button
              type="button"
              className="cancel-invite"
              onClick={()=> onCancel(member.id)}
            >
              <SafeIcon icon={FiTrash2} />
              Cancel invite
            </button>
          </>
        ) : (
          <span>Account owner</span>
        )}
      </div>
    </motion.article>
  );
}

function InviteModal({brand,onClose,onInvite}) {
  const [email,setEmail]=useState('');
  const [role,setRole]=useState('Editor');
  const validEmail=/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const submit=(event)=> {
    event.preventDefault();

    if (!validEmail) {
      return;
    }

    onInvite({
      email:email.trim(),
      name:'',
      role,
      status:'pending'
    });
  };

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <motion.section
        className="team-invite-modal"
        onMouseDown={(event)=> event.stopPropagation()}
        initial={{opacity:0,scale:0.96,y:10}}
        animate={{opacity:1,scale:1,y:0}}
      >
        <button
          type="button"
          className="team-modal-close"
          aria-label="Close invite form"
          onClick={onClose}
        >
          <SafeIcon icon={FiX} />
        </button>

        <span className="eyebrow">Invite teammate</span>
        <h2>Bring someone into the studio.</h2>
        <p>
          Choose a role label for this preview. No permissions will be
          enforced.
        </p>

        <form className="team-invite-form" onSubmit={submit}>
          <label>
            Email address
            <input
              type="email"
              value={email}
              onChange={(event)=> setEmail(event.target.value)}
              placeholder="teammate@example.com"
              autoComplete="email"
              autoFocus
              required
            />
          </label>

          <label>
            Role
            <select
              value={role}
              onChange={(event)=> setRole(event.target.value)}
            >
              {roles.map((item)=> (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </label>

          <div className="team-modal-actions">
            <button
              type="button"
              className="text-button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={!validEmail}
              style={{background:brand.primary}}
            >
              <SafeIcon icon={FiUserPlus} />
              Send invite
            </button>
          </div>
        </form>

        <small className="team-mock-note">
          Frontend preview · No invitation email will be sent
        </small>
      </motion.section>
    </div>
  );
}

function TeamScreen({
  brand,
  members,
  onInvite,
  onCancelInvite
}) {
  const [inviting,setInviting]=useState(false);
  const [resentId,setResentId]=useState(null);

  const invite=(member)=> {
    onInvite(member);
    setInviting(false);
  };

  const resend=(memberId)=> {
    setResentId(memberId);
    window.setTimeout(()=> setResentId(null),1600);
  };

  const activeCount=members.filter(
    (member)=> member.status==='active'
  ).length;
  const pendingCount=members.length-activeCount;

  return (
    <motion.main
      className="page team-page"
      initial={{opacity:0}}
      animate={{opacity:1}}
    >
      <header className="team-header">
        <div>
          <span className="eyebrow">Account workspace</span>
          <h1>Team</h1>
          <p>
            Keep the people helping shape your brands in one calm workspace.
          </p>
        </div>

        <button
          type="button"
          className="primary-button"
          style={{background:brand.primary}}
          onClick={()=> setInviting(true)}
        >
          <SafeIcon icon={FiUserPlus} />
          Invite teammate
        </button>
      </header>

      <section className="team-panel">
        <div className="team-summary">
          <div>
            <span className="eyebrow">Workspace access</span>
            <h2>People in DalePost</h2>
          </div>
          <div className="team-counts">
            <span>{activeCount} active</span>
            <i aria-hidden="true" />
            <span>{pendingCount} pending</span>
          </div>
        </div>

        <div className="team-list-heading" aria-hidden="true">
          <span>Member</span>
          <span>Role</span>
          <span>Status</span>
          <span>Actions</span>
        </div>

        <div className="team-list">
          {members.map((member)=> (
            <div className="team-row-wrap" key={member.id}>
              <MemberRow
                member={member}
                onResend={resend}
                onCancel={onCancelInvite}
              />
              {resentId===member.id && (
                <motion.span
                  className="team-resend-note"
                  initial={{opacity:0,y:-4}}
                  animate={{opacity:1,y:0}}
                >
                  Mock invitation resent
                </motion.span>
              )}
            </div>
          ))}
        </div>
      </section>

      <div className="team-access-note">
        <SafeIcon icon={FiUsers} />
        <p>
          Roles and invitations are labels only in this frontend preview.
          No account access or permissions are created.
        </p>
      </div>

      {inviting && (
        <InviteModal
          brand={brand}
          onClose={()=> setInviting(false)}
          onInvite={invite}
        />
      )}
    </motion.main>
  );
}

export default TeamScreen;